import generateActions from 'utils/actions';

export const GET_AUTHORS = generateActions('author/GET_AUTHORS');
export const DELETE_ALBUM = generateActions('author/DELETE_ALBUM');
export const DELETE_SONG = generateActions('author/DELETE_SONG');
export const GET_ALBUMS = generateActions('author/GET_ALBUMS');
export const GET_ALBUM = generateActions('author/GET_ALBUM');
export const POST_ALBUM = generateActions('author/PUT_ALBUM');
export const PUT_ALBUM = generateActions('author/PUT_ALBUM');
export const POST_SONG = generateActions('author/POST_SONG');
export const PUT_SONG = generateActions('author/PUT_SONG');
export const GET_POST = generateActions('author/GET_POST');
export const POST_POST = generateActions('author/POST_POST');
export const DELETE_POST = generateActions('author/DELETE_POST');
export const PUT_POST = generateActions('author/PUT_POST');
export const GET_POSTS = generateActions('author/GET_POSTS');
export const SET_ALBUM_FIELD = 'author/SET_ALBUM_FIELD';
export const SET_SINGLE_FIELD = 'author/SET_SINGLE_FIELD';
export const SET_POST_FIELD = 'author/SET_POST_FIELD';
export const CLEAR_ALBUM = 'author/CLEAR_ALBUM';
export const CLEAR_POST = 'author/CLEAR_POST';
export const SET_AUTHOR = 'author/SET_AUTHOR';

/**
 * Load all albums for the current user
 * @param {Params} params 
 */
export function loadMyAlbums(params) {
  return {
    types: GET_ALBUMS,
    promise: {
      url: `/albums`,
      data: params,
    },
  };
}


/**
 * Load the given album
 * @param {Number} albumId 
 */
export function loadAlbum(albumId) {
  return {
    types: GET_ALBUM,
    promise: {
      url: `/albums/${albumId}`,
    },
  };
}

/**
 * Search for authors by the given query
 * @param {String} query 
 */
export function loadAuthors(query) {
  return {
    types: GET_AUTHORS,
    promise: {
      url: `/search/artists`,
      data: {
        query,
      }
    },
  };
}

/**
 * Set the value of a field into the album state
 * @param {String} name 
 * @param {String} value 
 */
export function setAlbumField(name, value) {
  return {
    type: SET_ALBUM_FIELD,
    payload: {
      name,
      value,
    },
  };
}

/**
 * Clears the album state
 */
export function clearAlbum() {
  return {
    type: CLEAR_ALBUM,
  };
}

/**
 * Creates a new album
 * @param {Album} album 
 * @param {String} type 
 */
export function postAlbum(album, type = 'album') {
  return {
    types: POST_ALBUM,
    promise: {
      url: `/albums`,
      method: 'POST',
      form: {
        type,
        id: album.id,
        // Podcasts are always published because individual episodes can be published separately
        published: type === 'podcast',
        title: album.title,
        description: album.description,
        copyright: album.copyright ? album.copyright : undefined,
        phonographic: album.phonographic ? album.phonographic : undefined,
        genres: album.genres.length > 0 ? album.genres.map(genre => genre.id) : undefined,
        image: album.art,
        release_date: album.release_date ? `${album.release_date}-01-31` : undefined,
      },
    },
  };
}

/**
 * Updates an album, it uses POST due to restrictions in PHP whe uploading files
 * @param {Album} album 
 * @param {String} type 
 */
export function putAlbum(album, type = 'album') {
  return {
    types: PUT_ALBUM,
    promise: {
      url: `/albums/${album.id}`,
      method: 'POST',
      form: {
        type,
        id: album.id,
        user_id: album.author.id,
        // Podcasts are always published, because authors can publish single episodes at the time
        published: type === 'podcast' ? true : album.published,
        title: album.title,
        description: album.description,
        copyright: album.copyright ? album.copyright : undefined,
        phonographic: album.phonographic ? album.phonographic : undefined,
        genres: album.genres.length > 0 ? album.genres.map(genre => genre.id) : undefined,
        image: album.art,
        release_date: album.release_date ? `${album.release_date}-01-31` : undefined,
      },
    },
  };
}

/**
 * Deletes the current album from the datbase
 * @param {Album} album 
 */
export function deleteAlbum(album) {
  return {
    types: DELETE_ALBUM,
    promise: {
      url: `/albums/${album.id}`,
      method: 'DELETE',
    },
    data: { album },
  };
}

/**
 * Adds a new song to the given album
 * @param {Number} albumId 
 * @param {Song} song 
 */
export function postSong(albumId, song, onProgress) {
  return {
    types: POST_SONG,
    promise: {
      url: `/albums/${albumId}/songs`,
      method: 'POST',
      onProgress,
      form: {
        album_id: albumId,
        title: song.title,
        tags: song.tags,
        lyric: song.lyric,
        description: song.description,
        audio: song.mp3.file,
        bpm: song.bpm && song.bpm.value,
        moods: song.moods && song.moods.map(obj => obj.value).join(','),
        instruments: song.instruments && song.instruments.map(obj => obj.value).join(','),
        vocals: song.vocals && song.vocals.map(obj => obj.value).join(','),
        published: song.published,
      },
    },
  };
}

/**
 * Updates the song data, but it doesn't upload a new file
 * @param {Number} albumId 
 * @param {Song} song 
 */
export function putSong(albumId, song, onProgress) {
  return {
    types: PUT_SONG,
    promise: {
      url: `/albums/${albumId}/songs/${song.id}`,
      method: 'POST',
      onProgress: song.mp3.file instanceof File ? onProgress : undefined,
      form: {
        album_id: albumId,
        title: song.title,
        tags: song.tags,
        lyric: song.lyric,
        description: song.description,
        bpm: song.bpm && song.bpm.value,
        moods: song.moods && song.moods.map(obj => obj.value).join(','),
        instruments: song.instruments && song.instruments.map(obj => obj.value).join(','),
        vocals: song.vocals && song.vocals.map(obj => obj.value).join(','),
        audio: song.mp3.file instanceof File ? song.mp3.file : undefined,
        published: song.published,
      },
    },
  };
}

/**
 * Deletes a song from an album
 * @param {Number} albumId
 * @param {Number} songId 
 */
export function deleteSong(albumId, songId) {
  return {
    types: DELETE_SONG,
    promise: {
      url: `/albums/${albumId}/songs/${songId}`,
      method: 'DELETE',
    },
    data: {
      albumId,
      songId,
    },
  }
}

/**
 * Creates a single by calling the album and song APIs
 * one after the other.
 * @param {Album} single 
 */
export function postSingle(single, onProgress) {
  const album = {
    ...single,
    song: {
      ...single.song,
      title: single.title,
    },
  };

  return dispatch => dispatch(postAlbum(album, 'single'))
    .then((response) => dispatch(postSong(response.payload.album.id, album.song, onProgress)));
}

/**
 * Updates a single
 * @param {Album} single 
 */
export function putSingle(single, onProgress) {
  const album = {
    ...single,
    song: {
      ...single.song,
      title: single.title,
    },
  };

  return dispatch => dispatch(putAlbum(album, 'single'))
    .then(() => {
      if (album.song.id) {
        return dispatch(putSong(album.id, album.song, onProgress));
      } else {
        return dispatch(postSong(album.id, album.song, onProgress));
      }
    });
}

/**
 * Sets the value for the given field in the single object
 * @param {String} name 
 * @param {Any} value 
 */
export function setSingleField(name, value) {
  return {
    type: SET_SINGLE_FIELD,
    payload: {
      name,
      value,
    },
  };
}

/**
 * Set the author for album, single, podcast or post
 * @param {User} author 
 * @returns 
 */
export function setAuthor(author) {
  return {
    type: SET_AUTHOR,
    payload: {
      author,
    },
  };
}

/**
 * Load all posts for admin users
 * @param {Object} filters 
 */
export function loadPosts(params) {
  return {
    types: GET_POSTS,
    promise: {
      url: '/posts',
      data: params,
    },
    data: params,
  };
}

/**
 * Loads a post to be edited by the author or admin
 * @param {String} id 
 */
export function loadPost(id) {
  return {
    types: GET_POST,
    promise: {
      url: `/posts/${id}`,
    },
  };
}

/**
 * Creates a new post
 * @param {Post} post 
 */
export function postPost(post) {
  return {
    types: POST_POST,
    promise: {
      url: '/posts',
      method: 'POST',
      form: {
        title: post.title,
        content: post.markdown,
        allow_comments: post.allow_comments,
        featured: post.featured,
        published: post.published,
        asset: post.asset instanceof File ? post.asset : undefined,
      },
    },
  };
}

/**
 * Updates the given post
 * @param {Post} post 
 */
export function putPost(post) {
  return {
    types: PUT_POST,
    promise: {
      url: `/posts/${post.id}`,
      method: 'POST',
      form: {
        id: post.id,
        title: post.title,
        content: post.markdown,
        allow_comments: post.allow_comments,
        featured: post.featured,
        published: post.published,
        asset: post.asset instanceof File ? post.asset : undefined,
      },
    },
  };
}

/**
 * Deletes the given post
 * @param {Post} post 
 */
export function deletePost(post) {
  return {
    types: DELETE_POST,
    promise: {
      url: `/posts/${post.id}`,
      method: 'DELETE',
    },
    data: {
      postId: post.id,
    },
  };
}

/**
 * Set the value of a field into the post state
 * @param {String} name 
 * @param {String} value 
 */
export function setPostField(name, value) {
  return {
    type: SET_POST_FIELD,
    payload: {
      name,
      value,
    },
  };
}

/**
 * Clears the global state for the post
 */
export function clearPost() {
  return (dispatch, getState) => {
    const user = getState().auth.user;

    dispatch({
      type: CLEAR_POST,
      payload: {
        author: user,
      },
    });
  };
}